import { Command } from "common/app/command-palette/types";
import { eventBus } from "common/event-bus";

export class CommandManager {
  private registeredCommandIds: Set<string> = new Set();

  /**
   * Updates the managed commands to match the new list.
   * Calculates the difference and emits events via the event bus.
   * Commands in the new list will either be added or updated.
   * Commands not in the new list will be removed.
   * @param newCommands The desired list of commands. Undefined is ignored.
   * Pass an empty array `[]` to remove all commands.
   */
  updateCommands(newCommands: Command[] | undefined): void {
    if (newCommands === undefined) {
      return;
    }

    const newCommandIds = new Set(newCommands.map((c) => c.id));

    const commandsToRemove: string[] = [];
    this.registeredCommandIds.forEach((oldId) => {
      if (!newCommandIds.has(oldId)) {
        commandsToRemove.push(oldId);
      }
    });

    if (commandsToRemove.length > 0) {
      eventBus.emit({
        type: "RemovePaletteCommands",
        payload: commandsToRemove,
      });
    }

    if (newCommands.length > 0) {
      eventBus.emit({
        type: "AddPaletteCommands",
        payload: newCommands,
      });
    }

    this.registeredCommandIds = newCommandIds;
  }

  /**
   * Removes all commands managed by this instance via the event bus
   * and clears the internal state.
   */
  cleanup(): void {
    if (this.registeredCommandIds.size > 0) {
      const idsToRemove = Array.from(this.registeredCommandIds);
      eventBus.emit({
        type: "RemovePaletteCommands",
        payload: idsToRemove,
      });
      this.registeredCommandIds.clear();
    }
  }

  /**
   * Gets the list of currently registered command IDs (for debugging/advanced use).
   */
  getRegisteredCommandIds(): string[] {
    return Array.from(this.registeredCommandIds);
  }
}
